import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, extractError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { TIERS, tierSubsequentLabel, canUseTier } from "@/lib/wordLimits";
import GuardianConsentBanner from "@/components/GuardianConsentBanner";

function caseBadgeLabel(c) {
  if (c.status === "awaiting_b") return `Awaiting ${c.participant_b_email || "B"}`;
  if (c.status === "completed") return "Completed";
  return `Stage ${c.current_stage}`;
}

const DELIVERY_BADGE = {
  opened: {
    label: "Opened",
    icon: "\u2713",
    className: "bg-[color:var(--surface-sage)] text-[color:var(--text-primary)] border border-[color:var(--line)]",
  },
  delivered: {
    label: "Delivered",
    icon: "\u2713",
    className: "bg-[color:var(--surface-sage)] text-[color:var(--text-primary)] border border-[color:var(--line)]",
  },
  bounced: {
    label: "Bounced",
    icon: "\u26A0",
    className: "bg-[color:var(--counter-error)]/10 text-[color:var(--counter-error)] border border-[color:var(--counter-error)]/30",
  },
};

function DeliveryBadge({ status, caseId }) {
  const cfg = DELIVERY_BADGE[status];
  if (!cfg) return null;
  return (
    <span
      className={`text-[10px] font-medium px-2 py-0.5 rounded-full inline-flex items-center gap-1 ${cfg.className}`}
      data-testid={`delivery-badge-${caseId}`}
      data-status={status}
      title={`Invite email ${cfg.label.toLowerCase()}`}
    >
      <span aria-hidden="true">{cfg.icon}</span>
      {cfg.label}
    </span>
  );
}

export default function DashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/cases");
      setCases(data);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => {
    refresh();
  }, [refresh]);

  return (
    <div className="max-w-5xl mx-auto px-6 py-12 fade-in" data-testid="dashboard-page">
      <GuardianConsentBanner />
      <div className="flex items-end justify-between flex-wrap gap-3 mb-8">
        <div>
          <p className="text-xs uppercase tracking-[0.18em] text-[color:var(--participant-a)] font-medium">
            Your dashboard
          </p>
          <h1 className="font-serif text-3xl md:text-4xl mt-1 text-[color:var(--text-primary)]">
            Welcome, {user?.name || user?.email}
          </h1>
          <p className="text-[color:var(--text-muted)] mt-1 text-sm">
            Tier: <b className="text-[color:var(--text-primary)]">{user?.tier || "free"}</b> · Subsequent turns:{" "}
            <b className="text-[color:var(--text-primary)]">{tierSubsequentLabel(user?.tier || "free")}</b>
          </p>
        </div>
        <button
          onClick={() => setShowNew(true)}
          className="px-5 py-2.5 rounded-full bg-[color:var(--participant-a)] text-white font-semibold hover:opacity-90 transition"
          data-testid="new-case-btn"
        >
          New case
        </button>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {loading && (
          <div className="text-[color:var(--text-muted)]" data-testid="dashboard-loading">
            Loading your cases…
          </div>
        )}
        {!loading && cases.length === 0 && (
          <div
            className="md:col-span-2 rounded-2xl border border-dashed border-[color:var(--line)] bg-white p-10 text-center"
            data-testid="dashboard-empty"
          >
            <div className="font-serif text-2xl mb-1">No cases yet</div>
            <div className="text-sm text-[color:var(--text-muted)] mb-4">
              Open a new case to begin a fair, structured conversation.
            </div>
            <button
              onClick={() => setShowNew(true)}
              className="px-5 py-2.5 rounded-full bg-[color:var(--participant-a)] text-white font-semibold"
              data-testid="dashboard-empty-cta"
            >
              Start your first case
            </button>
          </div>
        )}
        {cases.map((c) => (
          <Link
            to={`/cases/${c.id}`}
            key={c.id}
            data-testid={`case-card-${c.id}`}
            className="rounded-2xl bg-white border border-[color:var(--line)] p-5 hover:shadow-sm transition block"
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-xs uppercase tracking-wider text-[color:var(--text-muted)]">
                  {c.tier} · {c.status}
                </div>
                <div className="font-serif text-xl mt-1 text-[color:var(--text-primary)]">
                  {c.title}
                </div>
              </div>
              <span className="flex flex-col items-end gap-1">
                <span className="text-xs px-2 py-1 rounded-full bg-[color:var(--surface-sage)]" data-testid={`case-badge-${c.id}`}>
                  {caseBadgeLabel(c)}
                </span>
                <DeliveryBadge status={c.delivery_status} caseId={c.id} />
              </span>
            </div>
            <div className="mt-3 flex items-center gap-4 text-sm">
              <span className="inline-flex items-center gap-2">
                <span className="inline-block w-2 h-2 rounded-full bg-[color:var(--participant-a)]" />
                {c.participant_a_name}
              </span>
              <span className="text-[color:var(--text-muted)]">vs</span>
              <span className="inline-flex items-center gap-2">
                <span className="inline-block w-2 h-2 rounded-full bg-[color:var(--participant-b)]" />
                {c.participant_b_name}
              </span>
            </div>
          </Link>
        ))}
      </div>

      {showNew && (
        <NewCaseModal
          userTier={user?.tier || "free"}
          isAdmin={user?.role === "admin"}
          onClose={() => setShowNew(false)}
          onCreated={(c) => {
            setShowNew(false);
            navigate(`/cases/${c.id}`);
          }}
        />
      )}
    </div>
  );
}

function NewCaseModal({ userTier, isAdmin, onClose, onCreated }) {
  const defaultTier = isAdmin ? "free" : (userTier || "free").toLowerCase();
  const [form, setForm] = useState({
    title: "",
    description: "",
    participant_a_name: "",
    participant_b_email: "",
    participant_a_country: "",
    participant_b_country: "",
    tier: defaultTier,
    custom_word_limit: 400,
  });
  // Draggable position — offset from the modal's natural (centered) location.
  // Persisted to localStorage so users keep their preferred spot across sessions.
  // Double-click the header (or press Reset) to snap back to the middle.
  const DRAG_STORAGE_KEY = "fairresolver.newCaseModal.position";
  const [drag, setDrag] = useState(() => {
    if (typeof window === "undefined") return { x: 0, y: 0 };
    try {
      const raw = window.localStorage.getItem(DRAG_STORAGE_KEY);
      if (!raw) return { x: 0, y: 0 };
      const parsed = JSON.parse(raw);
      const x = Number.isFinite(parsed?.x) ? parsed.x : 0;
      const y = Number.isFinite(parsed?.y) ? parsed.y : 0;
      return { x, y };
    } catch (err) {
      console.warn("Could not read saved modal position:", err);
      return { x: 0, y: 0 };
    }
  });
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      if (drag.x === 0 && drag.y === 0) {
        window.localStorage.removeItem(DRAG_STORAGE_KEY);
      } else {
        window.localStorage.setItem(DRAG_STORAGE_KEY, JSON.stringify(drag));
      }
    } catch (err) { console.warn("Could not persist modal position:", err); }
  }, [drag]);
  const dragState = React.useRef({ active: false, startX: 0, startY: 0, baseX: 0, baseY: 0 });
  const onDragStart = (e) => {
    if (e.button !== undefined && e.button !== 0) return;
    dragState.current = {
      active: true,
      startX: e.clientX,
      startY: e.clientY,
      baseX: drag.x,
      baseY: drag.y,
    };
    e.preventDefault();
    document.body.style.userSelect = "none";
  };
  const onDragMove = React.useCallback((e) => {
    if (!dragState.current.active) return;
    setDrag({
      x: dragState.current.baseX + (e.clientX - dragState.current.startX),
      y: dragState.current.baseY + (e.clientY - dragState.current.startY),
    });
  }, []);
  const onDragEnd = React.useCallback(() => {
    if (!dragState.current.active) return;
    dragState.current.active = false;
    document.body.style.userSelect = "";
  }, []);
  useEffect(() => {
    window.addEventListener("mousemove", onDragMove);
    window.addEventListener("mouseup", onDragEnd);
    return () => {
      window.removeEventListener("mousemove", onDragMove);
      window.removeEventListener("mouseup", onDragEnd);
    };
  }, [onDragMove, onDragEnd]);
  const resetPosition = () => setDrag({ x: 0, y: 0 });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [created, setCreated] = useState(null); // {invite_code, join_link, ...}
  const [copied, setCopied] = useState(false);

  const update = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const payload = { ...form };
      if (payload.tier !== "professional") delete payload.custom_word_limit;
      if (!payload.participant_a_name) delete payload.participant_a_name;
      const { data } = await api.post("/cases", payload);
      setCreated(data);
    } catch (err) {
      setError(extractError(err, "Could not create case"));
    } finally {
      setLoading(false);
    }
  };

  const copyJoinLink = async () => {
    if (!created?.join_link) return;
    try {
      await navigator.clipboard.writeText(created.join_link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) { console.warn("Could not copy join link:", err); }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm overflow-y-auto p-4 md:p-8"
      data-testid="new-case-modal"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ transform: `translate(${drag.x}px, ${drag.y}px)` }}
        className="w-full max-w-lg bg-white rounded-2xl border border-[color:var(--line)] shadow-xl mx-auto my-4 md:my-8 relative"
        data-testid="new-case-modal-panel"
      >
        <div
          onMouseDown={onDragStart}
          onDoubleClick={resetPosition}
          data-testid="new-case-modal-drag-handle"
          title="Drag to move · Double-click to recenter"
          className="cursor-move select-none rounded-t-2xl bg-[color:var(--surface-sage)] border-b border-[color:var(--line)] px-6 py-3 flex items-center justify-between gap-3"
        >
          <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-[color:var(--text-muted)]">
            <span className="inline-flex flex-col gap-[3px]" aria-hidden="true">
              <span className="block w-6 h-[2px] bg-[color:var(--text-muted)]/60 rounded-full" />
              <span className="block w-6 h-[2px] bg-[color:var(--text-muted)]/60 rounded-full" />
            </span>
            <span>{created ? "Invite ready" : "New case"}</span>
          </div>
          <div className="flex items-center gap-2">
            {(drag.x !== 0 || drag.y !== 0) && (
              <button
                type="button"
                onClick={resetPosition}
                className="text-xs text-[color:var(--text-muted)] hover:text-[color:var(--text-primary)] underline underline-offset-2"
                data-testid="new-case-modal-recenter"
              >
                Recenter
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              data-testid="new-case-modal-close"
              className="w-7 h-7 rounded-full hover:bg-white flex items-center justify-center text-[color:var(--text-muted)] hover:text-[color:var(--text-primary)]"
            >
              <span aria-hidden="true">×</span>
            </button>
          </div>
        </div>
        <div className="p-7">
        {created ? (
          <div data-testid="invite-success">
            <h2 className="font-serif text-2xl mb-1">Invite Participant B</h2>
            <p className="text-sm text-[color:var(--text-muted)] mb-4">
              Share this 6-digit code with{" "}
              <b className="text-[color:var(--text-primary)]">{created.participant_b_email}</b>.
              Email auto-send is coming soon — for now copy the link below.
            </p>
            <div className="rounded-2xl bg-[color:var(--surface-sage)] border border-[color:var(--line)] p-5 mb-4">
              <div className="text-xs uppercase tracking-wider text-[color:var(--text-muted)]">
                Invite code
              </div>
              <div
                className="font-serif text-4xl tracking-[0.35em] text-[color:var(--text-primary)] mt-1"
                data-testid="invite-code-display"
              >
                {created.invite_code}
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap mb-2">
              <code
                className="text-xs bg-white border border-[color:var(--line)] rounded-lg px-3 py-2 break-all flex-1 min-w-[260px]"
                data-testid="invite-join-link"
              >
                {created.join_link}
              </code>
              <button
                type="button"
                onClick={copyJoinLink}
                className="px-3 py-2 rounded-full bg-white border border-[color:var(--line)] text-xs font-medium hover:bg-[color:var(--surface-sage)]"
                data-testid="invite-copy-link"
              >
                {copied ? "Copied!" : "Copy link"}
              </button>
            </div>
            <div className="flex items-center justify-end gap-2 mt-5">
              <button
                onClick={onClose}
                className="px-4 py-2 rounded-full border border-[color:var(--line)] bg-white text-sm font-medium"
                data-testid="invite-close"
              >
                Close
              </button>
              <button
                onClick={() => onCreated(created)}
                className="px-5 py-2 rounded-full bg-[color:var(--participant-a)] text-white text-sm font-semibold hover:opacity-90"
                data-testid="invite-open-case"
              >
                Open case
              </button>
            </div>
          </div>
        ) : (
          <>
        <h2 className="font-serif text-2xl mb-1">Open a new case</h2>
        <p className="text-sm text-[color:var(--text-muted)] mb-4">
          Both participants will start with the 400-word Initial Story.
        </p>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="text-sm text-[color:var(--text-muted)] mb-1 block">Case title</label>
            <input
              required
              value={form.title}
              onChange={update("title")}
              className="w-full px-4 py-2.5 rounded-xl border border-[color:var(--line)] bg-white outline-none focus:ring-2 focus:ring-[color:var(--participant-a)]/30"
              data-testid="case-title"
            />
          </div>
          <div>
            <label className="text-sm text-[color:var(--text-muted)] mb-1 block">Short description (optional)</label>
            <input
              value={form.description}
              onChange={update("description")}
              className="w-full px-4 py-2.5 rounded-xl border border-[color:var(--line)] bg-white outline-none focus:ring-2 focus:ring-[color:var(--participant-a)]/30"
              data-testid="case-description"
            />
          </div>
          <div>
            <label className="text-sm text-[color:var(--text-muted)] mb-1 block">Your display name (optional)</label>
            <input
              value={form.participant_a_name}
              onChange={update("participant_a_name")}
              placeholder="Defaults to your account name"
              className="w-full px-4 py-2.5 rounded-xl border border-[color:var(--line)] bg-white outline-none focus:ring-2 focus:ring-[color:var(--participant-a)]/30"
              data-testid="case-pa-name"
            />
          </div>
          <div>
            <label className="text-sm text-[color:var(--text-muted)] mb-1 block">Participant B email</label>
            <input
              type="email"
              required
              value={form.participant_b_email}
              onChange={update("participant_b_email")}
              placeholder="bob@example.com"
              className="w-full px-4 py-2.5 rounded-xl border border-[color:var(--line)] bg-white outline-none focus:ring-2 focus:ring-[color:var(--participant-b)]/30"
              data-testid="case-pb-email"
            />
            <p className="text-xs text-[color:var(--text-muted)] mt-1">
              We'll generate a 6-digit invite code — share it with them so they
              can sign in on their own device and start their side.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm text-[color:var(--text-muted)] mb-1 block">Your country (optional)</label>
              <input
                value={form.participant_a_country}
                onChange={update("participant_a_country")}
                placeholder="Legal residence"
                className="w-full px-4 py-2.5 rounded-xl border border-[color:var(--line)] bg-white outline-none focus:ring-2 focus:ring-[color:var(--participant-a)]/30"
                data-testid="case-pa-country"
              />
            </div>
            <div>
              <label className="text-sm text-[color:var(--text-muted)] mb-1 block">Their country (optional)</label>
              <input
                value={form.participant_b_country}
                onChange={update("participant_b_country")}
                placeholder="Legal residence"
                className="w-full px-4 py-2.5 rounded-xl border border-[color:var(--line)] bg-white outline-none focus:ring-2 focus:ring-[color:var(--participant-b)]/30"
                data-testid="case-pb-country"
              />
            </div>
          </div>
          <p className="text-xs text-[color:var(--text-muted)] -mt-1">
            For professional cases, mediators must be registered with the
            national regulator of at least one party's country of legal
            residence.
          </p>
          <div>
            <label className="text-sm text-[color:var(--text-muted)] mb-1 block">Tier</label>
            <select
              value={form.tier}
              onChange={update("tier")}
              className="w-full px-4 py-2.5 rounded-xl border border-[color:var(--line)] bg-white outline-none focus:ring-2 focus:ring-[color:var(--participant-a)]/30"
              data-testid="case-tier"
            >
              {TIERS.map((t) => {
                const locked = !isAdmin && !canUseTier(userTier, t.key);
                return (
                  <option
                    key={t.key}
                    value={t.key}
                    disabled={locked}
                    data-testid={`case-tier-option-${t.key}`}
                  >
                    {t.name} · {t.key === "professional" ? "custom turn limit" : `${tierSubsequentLabel(t.key)} per turn`}
                    {locked ? " — upgrade required" : ""}
                  </option>
                );
              })}
            </select>
            {!isAdmin && !canUseTier(userTier, "professional") && (
              <p
                className="text-xs text-[color:var(--text-muted)] mt-1"
                data-testid="case-tier-upgrade-hint"
              >
                Your account is on the <b>{userTier}</b> tier. Higher tiers unlock longer turns
                and, at Professional, custom limits with a mediator.
              </p>
            )}
          </div>
          {form.tier === "professional" && (
            <div>
              <label className="text-sm text-[color:var(--text-muted)] mb-1 block">Custom subsequent turn limit</label>
              <input
                type="number"
                min="50"
                max="2000"
                value={form.custom_word_limit}
                onChange={update("custom_word_limit")}
                className="w-full px-4 py-2.5 rounded-xl border border-[color:var(--line)] bg-white outline-none focus:ring-2 focus:ring-[color:var(--participant-a)]/30"
                data-testid="case-custom-limit"
              />
            </div>
          )}
          {error && (
            <div className="text-sm text-[color:var(--counter-error)]" data-testid="case-create-error">
              {error}
            </div>
          )}
          <div className="flex items-center justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 rounded-full border border-[color:var(--line)] bg-white text-sm font-medium" data-testid="case-cancel">
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-5 py-2 rounded-full bg-[color:var(--participant-a)] text-white text-sm font-semibold hover:opacity-90 disabled:opacity-50"
              data-testid="case-create-submit"
            >
              {loading ? "Creating..." : "Create case"}
            </button>
          </div>
        </form>
          </>
        )}
        </div>
      </div>
    </div>
  );
}
